import * as React from "react"

const STORAGE_KEY = "aseguis_cookie_preferences"
const OPEN_EVENT = "aseguis:open-cookie-settings"
const UPDATE_EVENT = "aseguis:cookie-preferences-updated"
const GTM_SCRIPT_ID = "aseguis-gtm-script"
const GTM_ID = "GTM-TPGMLV4W"
const PREFERENCES_UPDATED_EVENT = UPDATE_EVENT

type CookiePreferences = {
    media: boolean;
    analytics: boolean;
    ads: boolean;
}

type UpdateOptions = {
    closeBanner?: boolean;
}

const defaultPreferences: CookiePreferences = {
    media: false,
    analytics: false,
    ads: false
}

const getSavedPreferences = (): CookiePreferences | null => {
    if (typeof window === "undefined") {
        return null
    }

    try {
        const rawValue = window.localStorage.getItem(STORAGE_KEY)
        if (!rawValue) {
            return null
        }

        const parsedValue = JSON.parse(rawValue)
        return {
            media: Boolean(parsedValue.media),
            analytics: Boolean(parsedValue.analytics),
            ads: Boolean(parsedValue.ads)
        }
    } catch {
        return null
    }
}

const persistPreferences = (preferences: CookiePreferences, options: UpdateOptions = {}) => {
    if (typeof window === "undefined") {
        return
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
    window.__aseguisCookiePreferences = preferences
    window.dispatchEvent(new CustomEvent(PREFERENCES_UPDATED_EVENT, {
        detail: {
            closeBanner: options.closeBanner ?? true
        }
    }))
}

const getConsentState = (preferences: CookiePreferences) => ({
    analytics_storage: preferences.analytics ? "granted" : "denied",
    ad_storage: preferences.ads ? "granted" : "denied",
    ad_user_data: preferences.ads ? "granted" : "denied",
    ad_personalization: preferences.ads ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    personalization_storage: "denied"
})

const ensureDataLayer = () => {
    window.dataLayer = window.dataLayer || []
    return window.dataLayer
}

const ensureGtag = () => {
    if (typeof window.gtag === "function") {
        return window.gtag
    }

    window.gtag = (...args: unknown[]) => {
        ensureDataLayer().push(args)
    }

    return window.gtag
}

const loadGtmScript = () => {
    if (window.__aseguisGtmLoaded || document.getElementById(GTM_SCRIPT_ID)) {
        return
    }

    const script = document.createElement("script")
    script.id = GTM_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
    script.onload = () => {
        window.__aseguisGtmLoaded = true
    }

    document.head.appendChild(script)
}

const removeCookie = (name: string, domain?: string) => {
    const domainPart = domain ? `domain=${domain};` : ""
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; ${domainPart}`
}

const clearTrackingCookies = (preferences: CookiePreferences) => {
    if (typeof document === "undefined") {
        return
    }

    const cookieNames = document.cookie
        .split(";")
        .map((cookie) => cookie.trim().split("=")[0])
        .filter(Boolean)

    const host = window.location.hostname
    const hostParts = host.split(".")
    const baseDomain = hostParts.length > 2 ? `.${hostParts.slice(-2).join(".")}` : `.${host}`
    const domains = [undefined, host, `.${host}`, baseDomain]

    cookieNames.forEach((cookieName) => {
        const isAnalyticsCookie = cookieName === "_ga" || cookieName.startsWith("_ga_")
        const isAdsCookie = cookieName.startsWith("_gcl_")

        if ((!preferences.analytics && isAnalyticsCookie) || (!preferences.ads && isAdsCookie)) {
            domains.forEach((domain) => removeCookie(cookieName, domain))
        }
    })
}

const applyPreferences = (preferences: CookiePreferences, options: UpdateOptions = {}) => {
    if (typeof window === "undefined") {
        return
    }

    persistPreferences(preferences, options)
    clearTrackingCookies(preferences)

    const hasOptionalConsent = preferences.analytics || preferences.ads
    if (!hasOptionalConsent && !window.__aseguisGtmLoaded) {
        return
    }

    const dataLayer = ensureDataLayer()
    const gtag = ensureGtag()

    if (!window.__aseguisGtmBootstrap) {
        dataLayer.push({ platform: "gatsby" })
        dataLayer.push({
            "gtm.start": new Date().getTime(),
            event: "gtm.js"
        })
        window.__aseguisGtmBootstrap = true
    }

    gtag("consent", "default", getConsentState(defaultPreferences))
    gtag("consent", "update", getConsentState(preferences))

    if (hasOptionalConsent) {
        loadGtmScript()
    }
}

const updateCookiePreferences = (preferences: CookiePreferences, options?: UpdateOptions) => {
    applyPreferences(preferences, options)
}

const CookieConsent = () => {
    const [preferences, setPreferences] = React.useState<CookiePreferences>(defaultPreferences)
    const [isVisible, setIsVisible] = React.useState(false)
    const [isReady, setIsReady] = React.useState(false)
    const [isConfiguring, setIsConfiguring] = React.useState(false)

    React.useEffect(() => {
        const savedPreferences = getSavedPreferences()
        if (savedPreferences) {
            setPreferences(savedPreferences)
            applyPreferences(savedPreferences)
        } else {
            setIsVisible(true)
        }

        setIsReady(true)
    }, [])

    React.useEffect(() => {
        const openSettings = () => {
            const savedPreferences = getSavedPreferences()
            if (savedPreferences) {
                setPreferences(savedPreferences)
            }
            setIsConfiguring(true)
            setIsVisible(true)
        }

        const syncPreferences = () => {
            const savedPreferences = getSavedPreferences()
            if (!savedPreferences) {
                return
            }

            setPreferences(savedPreferences)
        }

        const handlePreferencesUpdate = (event: Event) => {
            syncPreferences()

            const shouldCloseBanner = (event as CustomEvent<{ closeBanner?: boolean }>).detail?.closeBanner ?? true
            if (shouldCloseBanner) {
                setIsConfiguring(false)
                setIsVisible(false)
            }
        }

        window.addEventListener(OPEN_EVENT, openSettings)
        window.addEventListener(UPDATE_EVENT, handlePreferencesUpdate)
        return () => {
            window.removeEventListener(OPEN_EVENT, openSettings)
            window.removeEventListener(UPDATE_EVENT, handlePreferencesUpdate)
        }
    }, [])

    const saveAndClose = (nextPreferences: CookiePreferences) => {
        setPreferences(nextPreferences)
        applyPreferences(nextPreferences, { closeBanner: true })
        setIsConfiguring(false)
        setIsVisible(false)
    }

    if (!isReady || !isVisible) {
        return null
    }

    return (
        <div className="cookie-consent" role="region" aria-labelledby="cookie-consent-title">
            <div className="cookie-consent__panel">
                <div className="cookie-consent__header">
                    <div className="cookie-consent__copy">
                        <p className="cookie-consent__eyebrow">Configuración de cookies</p>
                        <h2 id="cookie-consent-title" className="cookie-consent__title">
                            {isConfiguring ? "Configura tus preferencias" : "Cookies y privacidad"}
                        </h2>
                        <p className="cookie-consent__text">
                            {isConfiguring
                                ? "Puedes activar o desactivar por categorías el contenido externo, la analítica y la medición publicitaria."
                                : "Usamos cookies necesarias. Tú eliges si permites contenido externo, analítica y publicidad."}
                        </p>
                    </div>

                    <div className="cookie-consent__actions cookie-consent__actions--desktop">
                        <button
                            type="button"
                            className="cookie-consent__button cookie-consent__button--secondary"
                            onClick={() => saveAndClose(defaultPreferences)}
                        >
                            Rechazar
                        </button>
                        {isConfiguring ? (
                            <button
                                type="button"
                                className="cookie-consent__button cookie-consent__button--secondary"
                                onClick={() => saveAndClose(preferences)}
                            >
                                Guardar preferencias
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="cookie-consent__button cookie-consent__button--secondary"
                                onClick={() => setIsConfiguring(true)}
                            >
                                Configurar
                            </button>
                        )}
                        <button
                            type="button"
                            className="cookie-consent__button cookie-consent__button--primary"
                            onClick={() => saveAndClose({ media: true, analytics: true, ads: true })}
                        >
                            Aceptar todas
                        </button>
                    </div>
                </div>

                {isConfiguring && (
                    <div className="cookie-consent__options">
                        <label className="cookie-consent__option">
                            <span>
                                <strong>Cookies técnicas</strong>
                                <small>Necesarias para el funcionamiento básico del sitio.</small>
                            </span>
                            <input type="checkbox" checked={true} disabled />
                        </label>

                        <label className="cookie-consent__option">
                            <span>
                                <strong>Contenido externo y vídeo</strong>
                                <small>Permiten reproducir el vídeo embebido desde Vimeo.</small>
                            </span>
                            <input
                                type="checkbox"
                                checked={preferences.media}
                                onChange={(event) => setPreferences((current) => ({
                                    ...current,
                                    media: event.target.checked
                                }))}
                            />
                        </label>

                        <label className="cookie-consent__option">
                            <span>
                                <strong>Cookies analíticas</strong>
                                <small>Nos ayudan a entender cómo se usa la web.</small>
                            </span>
                            <input
                                type="checkbox"
                                checked={preferences.analytics}
                                onChange={(event) => setPreferences((current) => ({
                                    ...current,
                                    analytics: event.target.checked
                                }))}
                            />
                        </label>

                        <label className="cookie-consent__option">
                            <span>
                                <strong>Cookies publicitarias</strong>
                                <small>Sirven para medir conversiones y campañas de Google Ads.</small>
                            </span>
                            <input
                                type="checkbox"
                                checked={preferences.ads}
                                onChange={(event) => setPreferences((current) => ({
                                    ...current,
                                    ads: event.target.checked
                                }))}
                            />
                        </label>
                    </div>
                )}

                <div className="cookie-consent__actions cookie-consent__actions--mobile">
                    <button
                        type="button"
                        className="cookie-consent__button cookie-consent__button--secondary"
                        onClick={() => saveAndClose(defaultPreferences)}
                    >
                        Rechazar
                    </button>
                    {isConfiguring ? (
                        <button
                            type="button"
                            className="cookie-consent__button cookie-consent__button--secondary"
                            onClick={() => saveAndClose(preferences)}
                        >
                            Guardar preferencias
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="cookie-consent__button cookie-consent__button--secondary"
                            onClick={() => setIsConfiguring(true)}
                        >
                            Configurar
                        </button>
                    )}
                    <button
                        type="button"
                        className="cookie-consent__button cookie-consent__button--primary"
                        onClick={() => saveAndClose({ media: true, analytics: true, ads: true })}
                    >
                        Aceptar todas
                    </button>
                </div>
            </div>
        </div>
    )
}

export { getSavedPreferences, OPEN_EVENT, UPDATE_EVENT, updateCookiePreferences }
export default CookieConsent
